import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";

import {
  getAttendanceChangeLogsForAdmin,
  getAllEmployees,
} from "@/config/queries/admin/admin-queries";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table/table";
import { Button } from "@/components/ui/button/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import { Skeleton } from "@/components/ui/skeleton/skeleton";

const STATUS_COLORS: Record<string, string> = {
  "Half-day": "bg-yellow-500 text-black",
  Present: "bg-green-500 text-black",
  Absent: "bg-red-500 text-white",
  "Paid Leave": "bg-cyan-500 text-black",
  "On Leave": "bg-orange-500 text-black",
  Holiday: "bg-blue-500 text-white",
  "Not Counting": "bg-gray-500 text-white",
};

const AttendanceChangeLogsPage = () => {
  const key = Cookies.get("key") as string;

  const [selectedEmployee, setSelectedEmployee] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const logsPerPage = 20;

  // Fetch all employees for filter
  const { data: employeesData } = useQuery({
    queryKey: ["all-employees"],
    queryFn: () => getAllEmployees(key),
  });

  // Fetch attendance change logs
  const { data: logsData, isPending: isLogsLoading } = useQuery({
    queryKey: [
      "attendance-change-logs",
      { employee: selectedEmployee, page: currentPage },
    ],
    queryFn: () =>
      getAttendanceChangeLogsForAdmin(key)({
        employee: selectedEmployee === "all" ? undefined : selectedEmployee,
        page: currentPage,
        limit: logsPerPage,
      }),
  });

  const logs = logsData?.logs || [];
  const pagination = logsData?.pagination;

  const handleEmployeeFilter = (value: string) => {
    setSelectedEmployee(value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Attendance Change Logs</h1>
        <p className="text-muted-foreground mt-1">
          View all attendance status changes made by admins
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Filter by Employee:</span>
          <Select value={selectedEmployee} onValueChange={handleEmployeeFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select employee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {employeesData?.employees
                ?.filter((emp) => !emp.blocked)
                ?.map((emp) => (
                  <SelectItem key={emp._id} value={emp._id}>
                    {emp.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        {pagination && (
          <div className="ml-auto text-sm text-muted-foreground">
            Showing {logs.length} of {pagination.total} logs
          </div>
        )}
      </div>

      {/* Logs Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Date & Time</TableHead>
              <TableHead>Admin</TableHead>
              <TableHead>Employee</TableHead>
              <TableHead>Attendance Date</TableHead>
              <TableHead>Status Change</TableHead>
              <TableHead className="min-w-[300px]">Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLogsLoading ? (
              // Loading skeleton
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                </TableRow>
              ))
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <p className="text-muted-foreground">
                    No attendance change logs found
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log._id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span>
                        {format(new Date(log.createdAtIST), "MMM dd, yyyy")}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(log.createdAtIST), "hh:mm a")}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{log.adminName}</span>
                      <span className="text-xs text-muted-foreground">
                        {log.adminEmail}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{log.employeeName}</span>
                  </TableCell>
                  <TableCell>
                    {format(new Date(log.attendanceDate), "MMM dd, yyyy")}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          STATUS_COLORS[log.previousStatus] || "bg-gray-200"
                        }`}
                      >
                        {log.previousStatus}
                      </span>
                      <span className="text-muted-foreground">&rarr;</span>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          STATUS_COLORS[log.newStatus] || "bg-gray-200"
                        }`}
                      >
                        {log.newStatus}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm line-clamp-2">{log.reason}</p>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(pagination.totalPages, prev + 1)
                )
              }
              disabled={currentPage === pagination.totalPages}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceChangeLogsPage;
